import { AmendCaloriesSheet } from "@/features/onboarding/components/amend-calories-sheet";
import { AmendMacrosSheet } from "@/features/onboarding/components/amend-macros-sheet";
import { OnboardingPillButton } from "@/features/onboarding/components/onboarding-pill-button";
import { OnboardingStepShellV5 } from "@/features/onboarding/components/onboarding-step-shell-v5";
import { useGetCalories } from "@/hooks/requests/nutritional-requirement/use-get-calories";
import { useGetMacros } from "@/hooks/requests/nutritional-requirement/use-get-macros";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { triggerHaptic } from "@/utils/haptics";
import { ReactNode, useState } from "react";

type ActiveSheet = "calories" | "macros" | null;

interface AmendPillProps {
  onClick: () => void;
  disabled?: boolean;
}

/** Light purple "Amend" pill from the Figma cards (983:6039 / 983:6040). */
const AmendPill = ({ onClick, disabled }: AmendPillProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex h-10 shrink-0 items-center justify-center rounded-[34px] bg-[#e5d5fb] px-4 text-sm font-bold text-[#180e34] shadow-[inset_0_-12px_6px_0_rgba(98,10,213,0.06)] disabled:opacity-40"
  >
    Amend
  </button>
);

/** Bordered dark card ("Main Container" in Figma 983:5978 / 983:5986). */
const TargetCard = ({ children }: { children: ReactNode }) => (
  <div className="w-full rounded-[32px] border border-[rgba(237,237,237,0.08)] px-5 py-6">
    {children}
  </div>
);

interface MacroRowProps {
  label: string;
  grams: number | undefined;
  percentage: number | undefined;
}

const MacroRow = ({ label, grams, percentage }: MacroRowProps) => (
  <div className="flex w-full items-center justify-between px-3 py-[13px]">
    <p className="text-base font-bold text-white">{label}</p>
    <p className="text-sm font-medium text-white">
      {grams != null && percentage != null
        ? `${Math.round(grams)} g (${Math.round(percentage)}%)`
        : "—"}
    </p>
  </div>
);

/**
 * "Your numbers. Worked out." (Figma 983:5954) — shows the computed targets and
 * lets the user amend them via the calorie/macros bottom sheets before the plan
 * is created (that happens on the app-preview screen's Continue).
 */
const BuildingConfirmationPage = () => {
  const { navigateToNextStep } = useStepRouteInfo();
  const { calories } = useGetCalories();
  const { macros } = useGetMacros();
  const [activeSheet, setActiveSheet] = useState<ActiveSheet>(null);

  const openSheet = (sheet: Exclude<ActiveSheet, null>) => {
    triggerHaptic();
    setActiveSheet(sheet);
  };

  const handleContinue = () => {
    triggerHaptic();
    navigateToNextStep();
  };

  return (
    <OnboardingStepShellV5
      title={
        <>
          I've worked out your numbers.
        </>
      }
      subtitle="Based on your goal and how you train. We can tweak them anytime."
      footer={<OnboardingPillButton text="Show me how it works" onClick={handleContinue} />}
    >
      <div className="flex flex-col gap-6">
        <TargetCard>
          <div className="flex w-full items-center justify-between">
            <p className="text-[22px] font-semibold text-white">
              {calories?.dailyCalories != null
                ? `${calories.dailyCalories.toLocaleString("en-GB")} kcals / day`
                : "—"}
            </p>
            <AmendPill onClick={() => openSheet("calories")} disabled={!calories} />
          </div>
          <p className="pt-1 text-base font-bold text-white/50">
            {calories?.weeklyCalories != null
              ? `${calories.weeklyCalories.toLocaleString("en-GB")} kcals / week`
              : "—"}
          </p>
        </TargetCard>

        <TargetCard>
          <div className="flex w-full items-center justify-between pb-3">
            <p className="text-[22px] font-semibold text-white">Macros Intake</p>
            <AmendPill onClick={() => openSheet("macros")} disabled={!macros} />
          </div>
          <MacroRow
            label="Protein"
            grams={macros?.proteinGrams}
            percentage={macros?.proteinPercentage}
          />
          <MacroRow label="Carbs" grams={macros?.carbsGrams} percentage={macros?.carbsPercentage} />
          <MacroRow label="Fats" grams={macros?.fatGrams} percentage={macros?.fatPercentage} />
        </TargetCard>
      </div>

      <AmendCaloriesSheet
        isOpen={activeSheet === "calories"}
        onDismiss={() => setActiveSheet(null)}
      />
      <AmendMacrosSheet isOpen={activeSheet === "macros"} onDismiss={() => setActiveSheet(null)} />
    </OnboardingStepShellV5>
  );
};

export default BuildingConfirmationPage;
